// uiarc: timeline (https://uiarc.dev/r/timeline.json) — CSS Modules → Tailwind token sınıfları, olay verisi yerine
// birleştirilebilir parçalar (`Timeline` > `TimelineGroup` > `TimelineItem`). Görünüm uiarc ile aynı: 28 px işaret
// sütunu, altında 1 px bağlayıcı çizgi, yapışkan gün başlığı, sağda sabit genişlikli zaman, satırın ilk satırı
// işaretle hizalı. Hareket: işaret yayla belirir, çizgi yukarıdan aşağı çizilir (görünüme ilk girişte, bir kez).
// `prefers-reduced-motion` altında hareket yok. Sapmalar: veri/gün gruplama/göreli zaman hesabı çağıranda;
// satır genişletme, kaydırma sönümü ve canlı duyuru yok; kademeli açılış saati yerine satır başına sabit gecikme.
import { motion, useReducedMotion } from 'motion/react'
import {
  useId,
  type HTMLAttributes,
  type LiHTMLAttributes,
  type OlHTMLAttributes,
  type ReactNode,
} from 'react'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

type TimelineTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

/** Ton → işaret görünümü (Badge ile aynı renk belirteçleri; `info` marka rengi). */
const markerTone: Record<TimelineTone, string> = {
  neutral: 'bg-surface text-text-secondary',
  success: 'bg-[color-mix(in_oklab,var(--success)_12%,var(--surface))] text-[color:var(--success)]',
  warning: 'bg-[color-mix(in_oklab,var(--warning)_12%,var(--surface))] text-[color:var(--warning)]',
  danger: 'bg-[color-mix(in_oklab,var(--danger)_12%,var(--surface))] text-[color:var(--danger)]',
  info: 'bg-[color-mix(in_oklab,var(--brand)_12%,var(--surface))] text-[color:var(--brand)]',
}

function Timeline({ className, ...props }: OlHTMLAttributes<HTMLOListElement>) {
  return (
    <ol
      data-slot="timeline"
      className={cn('text-foreground m-0 grid min-w-0 list-none p-0', className)}
      {...props}
    />
  )
}

type TimelineGroupProps = Omit<HTMLAttributes<HTMLLIElement>, 'children'> & {
  /** Grup başlığı (ör. gün). */
  label: ReactNode
  /** Başlığın sağında soluk sayaç (ör. "3 kayıt"). */
  count?: ReactNode
  /** Başlık düzeyi (`aria-level`). */
  headingLevel?: 2 | 3 | 4 | 5 | 6
  children?: ReactNode
}

/** Başlık, altındaki satırlar kayarken yerinde kalır. Başka bir yüzey üzerindeyse `--timeline-surface` verilir. */
function TimelineGroup({
  label,
  count,
  headingLevel = 3,
  className,
  children,
  ...props
}: TimelineGroupProps) {
  const headingId = useId()
  return (
    <li
      data-slot="timeline-group"
      aria-labelledby={headingId}
      className={cn('relative list-none', className)}
      {...props}
    >
      <div
        id={headingId}
        role="heading"
        aria-level={headingLevel}
        data-slot="timeline-group-label"
        className="leading-body sticky top-0 z-[2] flex items-baseline justify-between gap-3 bg-[var(--timeline-surface,var(--surface))] py-2 pr-3 text-sm font-medium"
      >
        <span>{label}</span>
        {count != null ? (
          <span className="text-text-muted text-xs font-normal tabular-nums">{count}</span>
        ) : null}
      </div>
      <ol className="m-0 mb-3 list-none p-0">{children}</ol>
    </li>
  )
}

type TimelineItemProps = Omit<LiHTMLAttributes<HTMLLIElement>, 'title'> & {
  /** İşaretin içindeki simge (14 px, çizgi 2). */
  icon: ReactNode
  tone?: TimelineTone
  title: ReactNode
  /** Sağdaki sabit genişlikli zaman etiketi. */
  time?: ReactNode
  /** Kim yaptı; başlığın altında soluk satırda (ör. Avatar + ad). */
  actor?: ReactNode
  /** Başlığın altında açılan gövde. */
  children?: ReactNode
}

function TimelineItem({
  icon,
  tone = 'neutral',
  title,
  time,
  actor,
  children,
  className,
  ...props
}: TimelineItemProps) {
  const reduce = useReducedMotion()
  // İlk kez görünüme girince işaret yayla belirir, çizgi ardından aşağı doğru çizilir.
  const reveal = { viewport: { once: true, amount: 0.2 } }
  return (
    <li
      data-slot="timeline-item"
      data-tone={tone}
      className={cn('group/timeline-item relative list-none pl-9', className)}
      {...props}
    >
      <motion.span
        data-slot="timeline-marker"
        data-tone={tone}
        aria-hidden="true"
        className={cn(
          'rounded-pill absolute top-2 left-0 z-[1] grid size-7 place-items-center after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:border after:border-[color:color-mix(in_oklab,var(--foreground)_10%,transparent)] [&_svg]:size-3.5 [&_svg]:stroke-2',
          markerTone[tone],
        )}
        initial={reduce ? false : { scale: 0.4, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        transition={{
          ...motionTokens.spring.morph,
          visualDuration: 0.36,
          opacity: { duration: motionTokens.duration.fast, ease: motionTokens.ease.enter },
        }}
        {...reveal}
      >
        {icon}
      </motion.span>
      {/* Çizgi işaretin altından sonraki işaretin üstüne uzanır; gruptaki son satırda yok. */}
      <motion.span
        data-slot="timeline-segment"
        aria-hidden="true"
        className="bg-border-strong absolute top-10 -bottom-1 left-[13.5px] w-px origin-top group-last/timeline-item:hidden"
        initial={reduce ? false : { scaleY: 0 }}
        whileInView={{ scaleY: 1 }}
        transition={{ ...motionTokens.spring.smooth, visualDuration: 0.34, delay: 0.1 }}
        {...reveal}
      />
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 px-3 py-3 max-[420px]:gap-x-2 max-[420px]:px-2">
        <div className="grid min-w-0 gap-0.5">
          <div
            data-slot="timeline-title"
            className="text-text-secondary [&_strong]:text-foreground text-sm leading-5 [&_strong]:font-medium"
          >
            {title}
          </div>
          {actor ? (
            <div
              data-slot="timeline-actor"
              className="text-text-muted leading-body flex items-center gap-1.5 text-xs"
            >
              {actor}
            </div>
          ) : null}
        </div>
        {time != null ? (
          <div
            data-slot="timeline-time"
            className="text-text-muted flex min-w-11 justify-end text-xs leading-5 whitespace-nowrap tabular-nums"
          >
            {time}
          </div>
        ) : null}
        {children ? (
          <div
            data-slot="timeline-body"
            className="text-text-secondary leading-body col-span-full mt-1 text-sm"
          >
            {children}
          </div>
        ) : null}
      </div>
    </li>
  )
}

export {
  Timeline,
  TimelineGroup,
  TimelineItem,
  type TimelineGroupProps,
  type TimelineItemProps,
  type TimelineTone,
}
